import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { usePageMeta } from '@/hooks/usePageMeta';
import { openCookieSettings } from '@/lib/cookieConsent';
import SmartBackLink from '@/components/common/SmartBackLink';

export default function CookiePolicy() {
  usePageMeta({
    fullTitle: true,
    title: 'Cookie Policy | LaneRunner',
    description: 'How LaneRunner uses cookies and similar technologies, the categories we set, and how to manage your preferences.',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="container mx-auto px-4 py-4">
          <SmartBackLink />
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-3">LaneRunner Cookie Consent</h1>
            <p className="text-muted-foreground">Effective Date: May 5, 2026</p>
          </div>

          <Card>
            <CardContent className="p-8 md:p-12">
              <div className="prose prose-neutral dark:prose-invert max-w-none text-muted-foreground leading-relaxed">
                <p>
                  LaneRunner uses cookies and similar technologies to keep the platform running, improve performance, and enhance your experience.
                </p>
                <p>
                  By using LaneRunner, you agree to the use of cookies as described below.
                </p>

                <section className="mb-8 mt-8">
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">What We Use Cookies For</h2>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Keeping you logged in</li>
                    <li>Processing payments securely</li>
                    <li>Saving your preferences and searches</li>
                    <li>Improving the platform through analytics</li>
                    <li>Detecting fraud and protecting accounts</li>
                    <li>Tracking referrals and marketing performance</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">Types of Cookies</h2>
                  <ul className="list-disc pl-6 space-y-1">
                    <li><strong className="text-foreground">Essential Cookies</strong> – Required for login, payments, and core features</li>
                    <li><strong className="text-foreground">Functional Cookies</strong> – Save preferences and improve usability</li>
                    <li><strong className="text-foreground">Analytics Cookies</strong> – Help us understand usage and improve</li>
                    <li><strong className="text-foreground">Marketing Cookies</strong> – Track referrals and campaign performance</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <p>
                    You can accept all cookies or choose to disable non-essential cookies in your browser settings. Note: disabling essential cookies may prevent the platform from working properly.
                  </p>
                  <p>
                    Your consent is stored and can be updated anytime in your account settings.
                  </p>
                  <div className="mt-6">
                    <Button onClick={openCookieSettings}>Manage Cookie Preferences</Button>
                  </div>
                </section>

                <section className="mb-2">
                  <p>
                    For full details, see our{' '}
                    <Link to="/privacy" className="text-primary underline">Privacy Policy</Link> and{' '}
                    <Link to="/terms" className="text-primary underline">Terms of Service</Link>.
                  </p>
                  <p>
                    Contact: <a href="mailto:support@lanerunner.com" className="text-primary underline">support@lanerunner.com</a>
                  </p>
                </section>

              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
